import { collection, doc, getDoc, getDocs, query, where, limit } from 'firebase/firestore';
import { firestore } from '../firebase';
import { getEventDate } from './eventUtils';

export function parseEditionYear(value) {
  const n = Number.parseInt(String(value || '').trim(), 10);
  return Number.isInteger(n) && n >= 2000 && n <= 9999 ? n : null;
}

export function getEventEditionYear(event) {
  if (!event) return null;
  const explicit = parseEditionYear(event.editionYear);
  if (explicit) return explicit;
  const date = getEventDate(event);
  return date ? date.getFullYear() : null;
}

/**
 * Public team dashboard / year live hub: `liveMode` on the event doc, with legacy fallback
 * to `publicLifecyclePhase === 'live'`.
 * @returns {'public'|'adminOnly'}
 */
export function getEffectiveLiveMode(event) {
  if (!event) return 'adminOnly';
  const lm = (event.liveMode || '').toString().trim().toLowerCase();
  if (lm === 'public') return 'public';
  if (lm === 'adminonly') return 'adminOnly';
  const phase = (event.publicLifecyclePhase || '').toString().toLowerCase();
  if (phase === 'live') return 'public';
  return 'adminOnly';
}

export function isEventLivePublic(event) {
  return getEffectiveLiveMode(event) === 'public';
}

export async function getEventByYear(year) {
  const y = parseEditionYear(year);
  if (!y) return null;
  const byEdition = await getDocs(query(collection(firestore, 'events'), where('editionYear', '==', y)));
  if (!byEdition.empty) {
    const d = byEdition.docs[0];
    return { id: d.id, ...d.data() };
  }
  const all = await getDocs(collection(firestore, 'events'));
  const list = all.docs.map((d) => ({ id: d.id, ...d.data() }));
  return list.find((e) => getEventEditionYear(e) === y) || null;
}

export async function getFeaturedYear() {
  let marketingFeaturedYear = null;
  let rootMode = 'autoLiveElseFeatured';
  try {
    const config = await getDoc(doc(firestore, '_config', 'publicSite'));
    if (config.exists()) {
      marketingFeaturedYear = parseEditionYear(config.data()?.marketingFeaturedYear);
      const rawMode = (config.data()?.rootMode || '').toString().trim();
      if (rawMode === 'featuredOnly' || rawMode === 'autoLiveElseFeatured') {
        rootMode = rawMode;
      }
    }
  } catch {
    // ignore; fall through to event-based fallback
  }

  // If an event is public-live, root should go to that year (unless explicitly disabled).
  if (rootMode === 'autoLiveElseFeatured') {
    try {
      const publicSnap = await getDocs(
        query(collection(firestore, 'events'), where('liveMode', '==', 'public'), limit(1))
      );
      if (!publicSnap.empty) {
        const ev = { id: publicSnap.docs[0].id, ...publicSnap.docs[0].data() };
        const y = getEventEditionYear(ev);
        if (y) return y;
      }
      const legacySnap = await getDocs(
        query(collection(firestore, 'events'), where('publicLifecyclePhase', '==', 'live'), limit(1))
      );
      if (!legacySnap.empty) {
        const ev = { id: legacySnap.docs[0].id, ...legacySnap.docs[0].data() };
        const y = getEventEditionYear(ev);
        if (y) return y;
      }
    } catch {
      // ignore; fall through
    }
  }

  if (marketingFeaturedYear) return marketingFeaturedYear;

  // Fallback to system activeEventId if set (migration away from isActive queries).
  try {
    const sys = await getDoc(doc(firestore, '_config', 'system'));
    const activeEventId = sys.exists() ? (sys.data()?.activeEventId || '') : '';
    if (activeEventId) {
      const evSnap = await getDoc(doc(firestore, 'events', String(activeEventId)));
      if (evSnap.exists()) {
        const ev = { id: evSnap.id, ...evSnap.data() };
        const y = getEventEditionYear(ev);
        if (y) return y;
      }
    }
  } catch {
    // ignore; fall through
  }

  // Legacy fallback (for older data/configs).
  try {
    const active = await getDocs(query(collection(firestore, 'events'), where('isActive', '==', true), limit(1)));
    if (!active.empty) {
      const ev = { id: active.docs[0].id, ...active.docs[0].data() };
      return getEventEditionYear(ev);
    }
  } catch {
    // ignore
  }

  const all = await getDocs(collection(firestore, 'events'));
  const list = all.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .map((e) => ({ ...e, _year: getEventEditionYear(e) }))
    .filter((e) => e._year != null)
    .sort((a, b) => b._year - a._year);
  return list[0]?._year || 2026;
}
